import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { FireForesightService } from 'src/api/services';
import { IncidentStatisticsByHour } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';

export interface HourlyStatRow {
  hourOfDay: number;
  totalIncidents: number;
  confirmed: number;
  singleDetections: number;
  multipleDetections: number;
  escalated: number;
}

@Component({
  selector: 'app-fire-foresight-statistics',
  templateUrl: './fire-foresight-statistics.component.html',
  styleUrls: ['./fire-foresight-statistics.component.scss']
})
export class FireForesightStatisticsComponent implements OnInit {

  Highcharts = Highcharts;
  chartConstructor = 'chart';

  loading = true;
  noData = false;
  lastRefresh: Date | null = null;
  reportPeriod = '';

  periodDays = 30;
  periodOptions = [7, 14, 30, 90];

  usingCustomRange = false;
  customStartDate: Date = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  customEndDate: Date = new Date();
  readonly bsDatePickerConfig: object = { dateInputFormat: 'D MMM YYYY', containerClass: 'theme-default' };

  totalIncidents = 0;
  totalConfirmed = 0;
  totalMultipleDetections = 0;
  totalEscalated = 0;
  confirmedPercent = '--';
  multipleDetectionsPercent = '--';
  escalatedPercent = '--';

  totalChartOptions: Highcharts.Options;
  statusChartOptions: Highcharts.Options;
  detectionsChartOptions: Highcharts.Options;
  escalationChartOptions: Highcharts.Options;
  detectionTypeChartOptions: Highcharts.Options;
  falsePositiveTypeChartOptions: Highcharts.Options;
  responsiblePartyChartOptions: Highcharts.Options;

  hourlyRows: HourlyStatRow[] = [];

  private readonly confirmedStatusName = 'Confirmed';
  private readonly categoryColors = [
    '#e53e3e', '#38a169', '#d97706', '#4299e1', '#805ad5', '#0bc5ea', '#dd6b20', '#38b2ac',
    '#d53f8c', '#a0aec0', '#48bb78', '#ecc94b', '#667eea', '#f56565', '#319795', '#b794f4'
  ];

  constructor(
    private svc: FireForesightService,
    public app: ApplicationConfigService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  setPeriod(days: number): void {
    this.usingCustomRange = false;
    if (this.periodDays === days) {
      return;
    }
    this.periodDays = days;
    this.load();
  }

  applyCustomRange(): void {
    if (!this.customStartDate || !this.customEndDate || this.customStartDate > this.customEndDate) {
      return;
    }
    this.usingCustomRange = true;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.noData = false;

    const end = this.usingCustomRange ? this.customEndDate : new Date();
    const start = this.usingCustomRange
      ? this.customStartDate
      : new Date(end.getTime() - this.periodDays * 24 * 60 * 60 * 1000);
    this.reportPeriod = `${start.toLocaleDateString()} \u2013 ${end.toLocaleDateString()}`;
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const numberOfDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));

    this.svc.FireForesightGetIncidentStatisticsByHour({
      customerId: this.app.customerId,
      timeZoneId,
      start: start.toISOString(),
      end: end.toISOString()
    }).subscribe(results => {
      this.buildReport(results || [], numberOfDays);
      this.loading = false;
      this.lastRefresh = new Date();
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private buildReport(results: IncidentStatisticsByHour[], numberOfDays: number): void {
    // Ensure every hour of the day (0-23) is represented, even when the API omits empty hours.
    const byHour = new Map<number, IncidentStatisticsByHour>();
    for (const row of results) {
      byHour.set(row.hourOfDay, row);
    }

    const hours: IncidentStatisticsByHour[] = [];
    for (let h = 0; h < 24; h++) {
      hours.push(byHour.get(h) || {
        hourOfDay: h,
        averageIncidents: 0,
        incidentsWithMultipleDetections: 0,
        incidentsEscalatedAtAnyPoint: 0,
        statusCounts: [],
        incidentCountsByDetectionType: [],
        detectionCountsByFalsePositiveType: [],
        detectionCountsByResponsibleParty: [],
        totalIncidents: 0,
        totalIncidentsEscalatedAtAnyPoint: 0,
        totalIncidentsWithMultipleDetections: 0
      });
    }

    this.noData = results.length === 0;

    this.totalIncidents = hours.reduce((sum, h) => sum + (h.totalIncidents || 0), 0);
    this.totalMultipleDetections = hours.reduce((sum, h) => sum + (h.totalIncidentsWithMultipleDetections || 0), 0);
    this.totalEscalated = hours.reduce((sum, h) => sum + (h.totalIncidentsEscalatedAtAnyPoint || 0), 0);
    this.totalConfirmed = hours.reduce((sum, h) => {
      const confirmed = (h.statusCounts || []).find(s => s.statusName?.toLowerCase() === this.confirmedStatusName.toLowerCase());
      return sum + (confirmed?.count || 0);
    }, 0);

    this.confirmedPercent = this.totalIncidents > 0
      ? `${((this.totalConfirmed / this.totalIncidents) * 100).toFixed(1)}%`
      : '--';
    this.multipleDetectionsPercent = this.totalIncidents > 0
      ? `${((this.totalMultipleDetections / this.totalIncidents) * 100).toFixed(1)}%`
      : '--';
    this.escalatedPercent = this.totalIncidents > 0
      ? `${((this.totalEscalated / this.totalIncidents) * 100).toFixed(1)}%`
      : '--';

    const categories = hours.map(h => `${h.hourOfDay.toString().padStart(2, '0')}:00`);

    this.totalChartOptions = this.buildColumnChart(
      categories,
      [{
        name: 'Average Incidents',
        data: hours.map(h => h.averageIncidents || 0),
        totals: hours.map(h => h.totalIncidents || 0),
        color: '#4299e1'
      }]
    );

    const statusNames = Array.from(new Set(
      hours.flatMap(h => (h.statusCounts || []).map(s => s.statusName))
    ));
    this.statusChartOptions = this.buildColumnChart(
      categories,
      statusNames.map((name, i) => {
        const totals = hours.map(h => (h.statusCounts || []).find(s => s.statusName === name)?.count || 0);
        return {
          name,
          data: totals.map(count => count / numberOfDays),
          totals,
          color: this.categoryColors[i % this.categoryColors.length]
        };
      }),
      true
    );

    const singleDetectionTotals = hours.map(h => Math.max(0, (h.totalIncidents || 0) - (h.totalIncidentsWithMultipleDetections || 0)));
    const multipleDetectionTotals = hours.map(h => h.totalIncidentsWithMultipleDetections || 0);
    this.detectionsChartOptions = this.buildColumnChart(
      categories,
      [
        { name: 'Single Detection', data: singleDetectionTotals.map(count => count / numberOfDays), totals: singleDetectionTotals, color: '#8892a4' },
        { name: 'Multiple Detections', data: hours.map(h => h.incidentsWithMultipleDetections || 0), totals: multipleDetectionTotals, color: '#e53e3e' }
      ],
      true
    );

    const escalatedTotals = hours.map(h => h.totalIncidentsEscalatedAtAnyPoint || 0);
    const notEscalatedTotals = hours.map((h, i) => Math.max(0, (h.totalIncidents || 0) - escalatedTotals[i]));
    this.escalationChartOptions = this.buildColumnChart(
      categories,
      [
        { name: 'Not Escalated', data: notEscalatedTotals.map(count => count / numberOfDays), totals: notEscalatedTotals, color: '#8892a4' },
        { name: 'Escalated', data: hours.map(h => h.incidentsEscalatedAtAnyPoint || 0), totals: escalatedTotals, color: '#d97706' }
      ],
      true
    );

    this.detectionTypeChartOptions = this.buildCategoryBreakdownChart(categories, hours, h => h.incidentCountsByDetectionType, numberOfDays);
    this.falsePositiveTypeChartOptions = this.buildCategoryBreakdownChart(categories, hours, h => h.detectionCountsByFalsePositiveType, numberOfDays);
    this.responsiblePartyChartOptions = this.buildCategoryBreakdownChart(categories, hours, h => h.detectionCountsByResponsibleParty, numberOfDays);
    this.hourlyRows = hours.map(h => {
      const confirmed = (h.statusCounts || []).find(s => s.statusName?.toLowerCase() === this.confirmedStatusName.toLowerCase())?.count || 0;
      const multipleDetections = h.totalIncidentsWithMultipleDetections || 0;
      return {
        hourOfDay: h.hourOfDay,
        totalIncidents: h.totalIncidents || 0,
        confirmed,
        singleDetections: Math.max(0, (h.totalIncidents || 0) - multipleDetections),
        multipleDetections,
        escalated: h.totalIncidentsEscalatedAtAnyPoint || 0
      };
    });
  }

  private buildCategoryBreakdownChart(
    categories: string[],
    hours: IncidentStatisticsByHour[],
    selector: (h: IncidentStatisticsByHour) => Array<{ categoryName: string; count: number }> | undefined,
    numberOfDays: number
  ): Highcharts.Options {
    const names = Array.from(new Set(hours.flatMap(h => (selector(h) || []).map(c => c.categoryName))));
    return this.buildColumnChart(
      categories,
      names.map((name, i) => {
        const totals = hours.map(h => (selector(h) || []).find(c => c.categoryName === name)?.count || 0);
        return {
          name,
          data: totals.map(count => count / numberOfDays),
          totals,
          color: this.categoryColors[i % this.categoryColors.length]
        };
      }),
      true
    );
  }

  private buildColumnChart(
    categories: string[],
    series: { name: string; data: number[]; totals?: number[]; color: string }[],
    stacked = false
  ): Highcharts.Options {
    const hasTotals = series.some(s => s.totals);
    return {
      chart: { type: 'column', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: { categories, title: { text: undefined } },
      yAxis: { title: { text: 'Average Incidents' }, allowDecimals: true },
      tooltip: {
        shared: true,
        formatter: hasTotals ? function (): string {
          const ctx = this as Highcharts.TooltipFormatterContextObject;
          const lines = [`<b>${ctx.x}</b>`];
          for (const point of ctx.points || []) {
            const total = (point.point.options as { total?: number }).total;
            const avg = Highcharts.numberFormat(point.y as number, 1);
            lines.push(`${point.series.name}: ${avg}${total != null ? ` (Total: ${total})` : ''}`);
          }
          return lines.join('<br/>');
        } : undefined
      },
      legend: { enabled: series.length > 1 },
      credits: { enabled: false },
      plotOptions: {
        column: stacked
          ? { stacking: 'normal', borderWidth: 0 }
          : { borderWidth: 0 }
      },
      series: series.map(s => ({
        name: s.name,
        type: 'column',
        data: s.totals ? s.data.map((avg, i) => ({ y: avg, total: s.totals[i] })) : s.data,
        color: s.color
      } as Highcharts.SeriesColumnOptions))
    };
  }
}
